// UI-only row actions for the admin users table. No data fetching: the page
// passes mutation callbacks and pending flags.
//
// Confirmations are Popconfirm-based (catalog pattern, cf. DeleteEntityButton):
// - block asks for an optional reason (goes to the audit metadata);
// - send-reset warns that the letter goes to the user's address;
// - platform-role toggle guards the last-admin rule with its own wording.
import { useState, type ReactElement } from 'react';
import { Button, Input, Popconfirm, Tooltip, message } from 'antd';
import {
  CheckCircleOutlined,
  CrownOutlined,
  MailOutlined,
  StopOutlined,
  UnlockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';

import type { AdminUserListItem, PlatformRole } from '../api/admin.client.js';
import { ApiError } from '../../../shared/api-client/index.js';

export interface AdminUserActionsProps {
  user: AdminUserListItem;
  onVerify: (userId: string) => Promise<unknown>;
  onBlock: (userId: string, reason?: string) => Promise<unknown>;
  onUnblock: (userId: string) => Promise<unknown>;
  onSendReset: (userId: string) => Promise<unknown>;
  onSetRole: (userId: string, role: PlatformRole) => Promise<unknown>;
  /** Disables every action while a mutation for this row is in flight. */
  pending?: boolean;
}

type Translate = (key: string) => string;

/** Maps known admin API error codes to localized messages. */
function actionErrorMessage(err: unknown, t: Translate): string {
  if (err instanceof ApiError) {
    if (err.code === 'last_admin_demote') {
      return t('crm.admin.errors.last_admin_demote');
    }
    if (err.code === 'not_platform_admin') {
      return t('crm.admin.errors.not_platform_admin');
    }
  }
  return t('crm.admin.errors.action_failed');
}

/** Runs a callback and surfaces rejections with a localized toast. */
async function runGuarded(action: () => Promise<unknown>, t: Translate): Promise<void> {
  try {
    await action();
  } catch (rejection: unknown) {
    void message.error(actionErrorMessage(rejection, t));
  }
}

export function AdminUserActions({
  user,
  onVerify,
  onBlock,
  onUnblock,
  onSendReset,
  onSetRole,
  pending = false,
}: AdminUserActionsProps): ReactElement {
  const { t } = useTranslation();
  const [blockReason, setBlockReason] = useState('');

  const verifyLabel = t('crm.admin.users.action_verify');
  const resetLabel = t('crm.admin.users.action_send_reset');

  return (
    <div className="flex items-center gap-1">
      <Popconfirm
        title={verifyLabel}
        description={t('crm.admin.users.confirm_verify', { email: user.email })}
        okText={verifyLabel}
        cancelText={t('crm.common.cancel')}
        okButtonProps={{ loading: pending }}
        disabled={user.emailVerified}
        onConfirm={() => runGuarded(() => onVerify(user.id), t)}
      >
        <Tooltip title={verifyLabel}>
          <Button
            size="small"
            type="text"
            disabled={pending || user.emailVerified}
            aria-label={verifyLabel}
            icon={<CheckCircleOutlined />}
          />
        </Tooltip>
      </Popconfirm>

      {user.isBlocked ? (
        <Popconfirm
          title={t('crm.admin.users.action_unblock')}
          description={t('crm.admin.users.confirm_unblock')}
          okText={t('crm.admin.users.action_unblock')}
          cancelText={t('crm.common.cancel')}
          okButtonProps={{ loading: pending }}
          onConfirm={() => runGuarded(() => onUnblock(user.id), t)}
        >
          <Tooltip title={t('crm.admin.users.action_unblock')}>
            <Button
              size="small"
              type="text"
              disabled={pending}
              aria-label={t('crm.admin.users.action_unblock')}
              icon={<UnlockOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      ) : (
        <Popconfirm
          title={t('crm.admin.users.action_block')}
          description={
            <div className="min-w-[220px]">
              <p className="mb-2 text-xs text-[var(--ant-color-text-secondary)]">
                {t('crm.admin.users.confirm_block', { email: user.email })}
              </p>
              <Input
                aria-label={t('crm.admin.users.block_reason_label')}
                placeholder={t('crm.admin.users.block_reason_placeholder')}
                value={blockReason}
                maxLength={500}
                onChange={(event) => setBlockReason(event.target.value)}
              />
            </div>
          }
          okText={t('crm.admin.users.action_block')}
          okButtonProps={{ danger: true, loading: pending }}
          cancelText={t('crm.common.cancel')}
          onConfirm={() => {
            const reason = blockReason.trim();
            setBlockReason('');
            return runGuarded(() => onBlock(user.id, reason.length > 0 ? reason : undefined), t);
          }}
        >
          <Tooltip title={t('crm.admin.users.action_block')}>
            <Button
              size="small"
              type="text"
              danger
              disabled={pending}
              aria-label={t('crm.admin.users.action_block')}
              icon={<StopOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      )}

      <Popconfirm
        title={resetLabel}
        description={t('crm.admin.users.confirm_send_reset', { email: user.email })}
        okText={resetLabel}
        cancelText={t('crm.common.cancel')}
        okButtonProps={{ loading: pending }}
        onConfirm={() => runGuarded(() => onSendReset(user.id), t)}
      >
        <Tooltip title={resetLabel}>
          <Button
            size="small"
            type="text"
            disabled={pending}
            aria-label={resetLabel}
            icon={<MailOutlined />}
          />
        </Tooltip>
      </Popconfirm>

      {user.platformRole === 'admin' ? (
        <Popconfirm
          title={t('crm.admin.users.action_demote')}
          description={t('crm.admin.users.confirm_demote')}
          okText={t('crm.admin.users.action_demote')}
          cancelText={t('crm.common.cancel')}
          okButtonProps={{ danger: true, loading: pending }}
          onConfirm={() => runGuarded(() => onSetRole(user.id, 'user'), t)}
        >
          <Tooltip title={t('crm.admin.users.action_demote')}>
            <Button
              size="small"
              type="text"
              disabled={pending}
              aria-label={t('crm.admin.users.action_demote')}
              icon={<UserOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      ) : (
        <Popconfirm
          title={t('crm.admin.users.action_promote')}
          description={t('crm.admin.users.confirm_promote')}
          okText={t('crm.admin.users.action_promote')}
          cancelText={t('crm.common.cancel')}
          okButtonProps={{ loading: pending }}
          onConfirm={() => runGuarded(() => onSetRole(user.id, 'admin'), t)}
        >
          <Tooltip title={t('crm.admin.users.action_promote')}>
            <Button
              size="small"
              type="text"
              disabled={pending}
              aria-label={t('crm.admin.users.action_promote')}
              icon={<CrownOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      )}
    </div>
  );
}
